import { useAsyncEffect } from 'ahooks';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { LoadingTip } from 'tea-component';
import { useIntl } from 'umi';
import classNames from 'classnames';
import useCssFullScreen from '../ChatPage/components/AIContent/hooks/useCssFullScreen';
import type { ChatDSTemplateActionsListProps } from '../ChatDSTemplate/type';
import { useUrlQueryParam } from '@/pages/aigc/hooks/useQuery';
import ExcelSheetTabs, { ISheet } from './components/ExcelSheetTabs';
import FileSelector, { IFileItem } from './components/FileSelector';
import SmartTableGrid, {
  IColumnConfig,
  ISmartTableGridRef,
  TTableRow,
} from './components/SmartTableGrid';
import TableActionsBar, {
  ITableActionsBarRef,
} from './components/TableActionsBar';
import { AIExcelActionType, useSmartTableChat, useAutoSave } from './hooks';
import {
  useGetExcelInfo,
} from './hooks/useGetExcelInfo';
import { ISheetData, useSheetData } from './hooks/useSheetData';
import { generateBlankSheetData } from './utils';
import './index.scss';
/**
 * 自定义 memo 比较函数：只比较 ChatSmartTable 真正使用的 props
 * 忽略 conversations / streamingState 等不相关的 props，
 * 避免流式输出时父组件传入新引用导致无效重渲染
 */
const areSmartTablePropsEqual = (prevProps: any, nextProps: any): boolean => {
  return (
    prevProps.sessionId === nextProps.sessionId &&
    prevProps.dsReport?.id === nextProps.dsReport?.id
  );
};

/**
 * 智能表格面板组件
 * 用于展示 AI 智能表格数据
 */
export const ChatSmartTable: React.FC<ChatDSTemplateActionsListProps> = memo(
  function ChatSmartTable(props: any) {
    const { handleUpdateDsReport, startNewConversation, dsReport, sessionId, readonly = false, onFocus, onHeaderMouseDown } =
      props;
    const { formatMessage } = useIntl();
    const gridRef = useRef<ISmartTableGridRef>(null);
    const toolbarRef = useRef<ITableActionsBarRef>(null);
    // 全屏容器 ref
    const fullScreenRef = useRef<HTMLDivElement>(null);
    const { isFullScreen, toggleFullScreen } = useCssFullScreen(fullScreenRef);
    // 用于清除 URL 中的 template=splitView 参数（智能表格临时分栏模式），与 AiGalleryPanel 关闭行为保持一致
    const [, setTemplateParam] = useUrlQueryParam(['template']);

    // 初始只有一个空白工作表
    const [sheets, setSheets] = useState<ISheet[]>([
      { id: '1', name: formatMessage({ id: 'dba_smart_table_blank_sheet' }) },
    ]);
    const [activeSheetId, setActiveSheetId] = useState<string>('1');
    // 当前选中的文件（用于 FileSelector 受控组件）
    const [currentFile, setCurrentFile] = useState<IFileItem | undefined>(undefined);

    // 记录是否已初始化，用于区分首次加载和后续文件切换
    const isInitializedRef = useRef<boolean>(false);

    // 常に最新の dsReport.id を保持する ref（レンダーごとに更新され、onComplete コールバック内で最新値を読める）
    const dsReportIdRef = useRef(dsReport?.id);
    dsReportIdRef.current = dsReport?.id;
    // AI 润色開始時の dsReport.id を記録。onComplete 発火時に比較し、
    // dsReport.id が変わっていれば useAsyncEffect が新ファイルを取得済みのためスキップ
    const dsReportIdAtPolishStartRef = useRef<string | undefined>(undefined);

    // 使用 useSheetData hook 管理每个 sheet 的数据
    const {
      sheetDataMap,
      getSheetData,
      setSheetData,
      handleSheetsChange: processSheetDataChange,
    } = useSheetData({
      initialSheets: sheets,
    });

    // 删除文件请求
    // const deleteFileRequest = useRequestController({
    //   method: 'POST',
    //   url: `/api/agent/delete_lke_file`,
    //   manual: true,
    //   httpCfg: {
    //     rawData: true,
    //     ignoreCode: true,
    //   },
    //   onSuccess: (response) => {
    //     showErrorTips?.(response?.res_code);
    //   },
    // });

    // 使用封装后的 hook，传入状态更新方法，hook 内部自动处理数据
    const { fetchExcelData, isLoading, currentFileName, currentFileCosUrl, data, refresh, fetchSingleFile } =
      useGetExcelInfo({
        setSheetData,
        setSheets,
        setActiveSheetId,
        activeSheetId,
        gridRef,
      });

    /**
     * 重新拉取并解析当前文件的最新数据。
     * AI 润色/智能填充等操作流式完成后に呼ばれ、表格を更新する。
     *
     * 呼び出し元（handleAIPolish 等）侧で、dsReport.id が操作開始時から
     * 変化していないことを確認してから呼ばれる。
     * （変化していた場合、useAsyncEffect が新しい dsReport.id で fetchExcelData を
     *   実行済みのため、こちらの呼び出しは不要）
     */
    const reloadCurrentExcel = useCallback(async () => {
      if (!isInitializedRef.current) return;
      const apiSessionId = (dsReport as any)?.sourceSessionId || sessionId;
      const fileId = currentFile?.id || dsReport?.id;
      if (!fileId) return;
      await fetchExcelData({
        sessionId: apiSessionId,
        targetFileId: fileId,
        // cos 标签来源：跳过 get_excel_info，直接读取 cos 链接；否则返回空时降级读取
        cosUrl: (dsReport as any)?.cos_url,
        isCosTag: (dsReport as any)?.isCosTag,
      });
    }, [currentFile?.id, dsReport, sessionId, fetchExcelData]);

    // 文件列表数据（基于 field_id 映射，包含默认初始值）
    const fileList = useMemo(() => {
      // 如果没有数据或文件列表为空，返回默认值
      if (!data?.files || data?.files?.length === 0) {
        return [];
      }

      // 根据 field_id 映射文件列表
      const fileList: IFileItem[] = data.files.map((file: any) => {
        const latestFile = file?.latest_file;
        return latestFile;
      });

      return fileList;
    }, [data?.files]);

    // 获取当前 sheet 的数据
    const currentSheetData = useMemo<ISheetData>(() => {
      const sheetData = getSheetData(activeSheetId);
      return sheetData || generateBlankSheetData();
    }, [activeSheetId, getSheetData]);

    // 当前 sheet 的 tableData 和 columns
    const { tableData, columns } = currentSheetData;

    // 当前活动工作表的名称
    const activeSheetName = useMemo(
      () => sheets.find((s) => s.id === activeSheetId)?.name || '',
      [sheets, activeSheetId],
    );

    // 表格名称（用于工具栏、AI 润色弹层显示和 prompt 上下文）
    // 使用 currentFileName 而非 dsReport?.name，因为用户切换文件后 dsReport 不会更新，
    // 导致 AI 润色弹层显示的表名与实际操作文件不一致
    const tableName = `「${currentFileName || dsReport?.name || ''}」 ${activeSheetName}`;
    
    // 自动保存 hook —— 离开编辑上下文时自动保存（通过快照对比自动判断是否有变化，快照由 hook 内部自动管理）
    const { flush: flushSave } = useAutoSave({
      sheetDataMap,
      sheets,
      sheetName: activeSheetName,
      currentFileName,
      currentFileCosUrl,
      readonly,
    });

    // 使用智能表格聊天 hook - 简化版
    const { sendSmartTableAction } = useSmartTableChat({
      startNewConversation,
      tableName,
      sheetName: activeSheetName,
      currentFileCosUrl,
    });

    // 当前选中的文件变化 - 切换前先保存旧数据，再下载并解析新文件
    const handleFileChange = useCallback(
      async (file: IFileItem) => {
        // 切换文件前，先保存当前编辑内容
        await flushSave();
        setCurrentFile(file);
        const fileId = file.id || '';
        if (!fileId) return;
        await fetchSingleFile(fileId, {
          name: file.name,
          cos_url: file.cos_url,
        });
      },
      [fetchSingleFile, flushSave],
    );

    // 表格数据变化回调 - 同步到 sheetDataMap（自动保存通过快照 diff 判断变化，无需手动标记）
    const handleDataChange = useCallback(
      (newData: TTableRow[], newColumns: IColumnConfig[]) => {
        setSheetData(activeSheetId, {
          tableData: newData,
          columns: newColumns,
        });
      },
      [activeSheetId, setSheetData],
    );

    // AI 修改列数据条件
    const handleAIModifyColumnCondition = useCallback(
      (colIndex: number, column: IColumnConfig, prompt: string) => {
        dsReportIdAtPolishStartRef.current = dsReportIdRef.current;
        sendSmartTableAction(
          AIExcelActionType.MODIFY_COLUMN_CONDITION,
          {
            colIndex,
            columnName: column?.title || '',
            prompt,
          },
          {
            onComplete: () => {
              if (dsReportIdAtPolishStartRef.current === dsReportIdRef.current) {
                void reloadCurrentExcel();
              }
            },
          },
        );
      },
      [sendSmartTableAction, reloadCurrentExcel],
    );

    // AI 调整数据格式
    const handleAIAdjustDataFormat = useCallback(
      (colIndex: number, column: IColumnConfig, prompt: string) => {
        dsReportIdAtPolishStartRef.current = dsReportIdRef.current;
        sendSmartTableAction(
          AIExcelActionType.ADJUST_COLUMN_FORMAT,
          {
            colIndex,
            columnName: column?.title || '',
            prompt,
          },
          {
            onComplete: () => {
              if (dsReportIdAtPolishStartRef.current === dsReportIdRef.current) {
                void reloadCurrentExcel();
              }
            },
          },
        );
      },
      [sendSmartTableAction, reloadCurrentExcel],
    );

    // 智能填充行回调
    const handleSmartFillRow = useCallback(
      (values: any) => {
        dsReportIdAtPolishStartRef.current = dsReportIdRef.current;
        sendSmartTableAction(
          AIExcelActionType.SMART_FILL_ROW,
          {
            prompt: values?.prompt || '',
          },
          {
            onComplete: () => {
              if (dsReportIdAtPolishStartRef.current === dsReportIdRef.current) {
                void reloadCurrentExcel();
              }
            },
          },
        );
      },
      [sendSmartTableAction, reloadCurrentExcel],
    );

    // 智能填充列回调
    const handleSmartFillColumn = useCallback(
      (values: any) => {
        dsReportIdAtPolishStartRef.current = dsReportIdRef.current;
        sendSmartTableAction(
          AIExcelActionType.SMART_FILL_COLUMN,
          {
            columnName: values?.column_name || '',
            prompt: values?.prompt || '',
          },
          {
            onComplete: () => {
              if (dsReportIdAtPolishStartRef.current === dsReportIdRef.current) {
                void reloadCurrentExcel();
              }
            },
          },
        );
      },
      [sendSmartTableAction, reloadCurrentExcel],
    );

    // 处理 sheets 变化（新增、删除、重命名、排序、创建副本）
    const handleSheetsChange = useCallback(
      (newSheets: ISheet[]) => {
        const cleanedSheets = processSheetDataChange(sheets, newSheets);
        setSheets(cleanedSheets);
      },
      [sheets, processSheetDataChange],
    );

    // 关闭面板
    const handleClose = useCallback(async () => {
      // 清除 URL 中的 template=splitView 参数：智能表格临时分栏模式下关闭面板时同步清理，与 AiGalleryPanel 一致
      const currentTemplate = new URLSearchParams(window.location.search).get('template');
      if (currentTemplate === 'splitView') {
        setTemplateParam({ template: undefined });
      }
      // Call delete_lke_file API before closing
      // const fileIds = data?.files?.map((file: any) => file?.latest_file?.id).filter(Boolean) || [];
      // if (sessionId && fileIds.length > 0) {
      //   try {
      //     await deleteFileRequest.runAsync({
      //       trace_id: uuidv4(),
      //       session_id: sessionId,
      //       ids: fileIds,
      //     });
      //   } catch (error) {
      //     console.error('delete_lke_file failed:', error);
      //   }
      // }
      handleUpdateDsReport({ show: false });
    }, [handleUpdateDsReport, setTemplateParam]);

    // ==================== AI 润色弹窗状态 ====================
    const [aiPolishVisible, setAiPolishVisible] = useState(false);

    const handleAIPolishClick = useCallback(() => {
      if (readonly) return;
      setAiPolishVisible((prev) => !prev);
    }, [readonly]);

    const handleAIPolishClose = useCallback(() => {
      setAiPolishVisible(false);
    }, []);

    // AI 润色回调
    const handleAIPolish = useCallback(
      (prompt: string) => {
        // 操作開始時の dsReport.id を記録。onComplete で dsReportIdRef（常に最新）と比較し、
        // 操作中に dsReport.id が変わった場合は useAsyncEffect が新ファイルを取得済みなのでスキップ
        dsReportIdAtPolishStartRef.current = dsReportIdRef.current;
        sendSmartTableAction(
          AIExcelActionType.AI_POLISH,
          { prompt },
          {
            onComplete: () => {
              // dsReport.id が操作開始時と変わっていなければ reloadCurrentExcel を実行
              if (dsReportIdAtPolishStartRef.current === dsReportIdRef.current) {
                void reloadCurrentExcel();
              }
            },
          },
        );
        setAiPolishVisible(false);
      },
      [sendSmartTableAction, reloadCurrentExcel],
    );

    // 保存成功后刷新 Excel 数据
    const handleSaveSuccess = useCallback(async () => {
      const fileId = currentFile?.id;
      if (!fileId) return;
      refresh();
    }, [currentFile?.id, refresh]);

    // 重置到原始文件
    const handleReset = useCallback(async () => {
      const fileId = currentFile?.id || dsReport?.id;
      if (!fileId) {
        console.warn('未找到文件 ID，无法重置');
        return;
      }
      // 使用 sourceSessionId（原始会话 ID），回退到 props.sessionId
      const apiSessionId = (dsReport as any)?.sourceSessionId || sessionId;
      await fetchExcelData({
        sessionId: apiSessionId,
        type: 'original',
        targetFileId: fileId,
        // cos 标签来源：跳过 get_excel_info，直接读取 cos 链接；否则返回空时降级读取
        cosUrl: (dsReport as any)?.cos_url,
        isCosTag: (dsReport as any)?.isCosTag,
      });
    }, [currentFile?.id, dsReport, fetchExcelData, sessionId]);

    // 自动下载表格数据：dsReport.id 变化时触发（包括初始化和 useAutoLayout 检测到新文件）
    // 使用 dsReport.sourceSessionId（原始聊天会话 ID），回退到 props.sessionId
    // 原因：在 CopilotReportWindow 中渲染时，props.sessionId 是 Copilot 的 sessionId，
    // 但 API 需要的是生成该文件的原始聊天会话 ID
    useAsyncEffect(async () => {
      const fileId = dsReport?.id;
      if (!fileId) return;

      // 非首次加载（dsReport.id 变化说明有新文件），先保存当前编辑内容
      if (isInitializedRef.current && !readonly) {
        try {
          await flushSave();
        } catch (error) {
          console.error('[SmartTable] 自动保存失败:', error);
        }
      }
      // 使用 sourceSessionId（原始会话 ID），回退到 props.sessionId
      const apiSessionId = (dsReport as any)?.sourceSessionId || sessionId;
      // 获取对应文件数据
      const result = await fetchExcelData({
        sessionId: apiSessionId,
        targetFileId: fileId,
        // cos 标签来源：跳过 get_excel_info，直接读取 cos 链接；否则返回空时降级读取
        cosUrl: (dsReport as any)?.cos_url,
        isCosTag: (dsReport as any)?.isCosTag,
      });
      // 设置当前选中文件
      if (result && dsReport?.name) {
        setCurrentFile({
          id: fileId,
          name: dsReport.name,
        });
      }
      isInitializedRef.current = true;
    }, [dsReport?.id, sessionId, dsReport?.sourceSessionId]);

    return (
      <>
        {/* 全屏遮罩层 */}
        {isFullScreen && <div className="dba-smart-table-fullscreen-backdrop" />}
        <div
          ref={fullScreenRef}
          className={classNames(
            'dba-chat-ds-report-page-right dba-chat-smart-table-page',
            isFullScreen && 'dba-smart-table-fullscreen'
          )}
          onMouseDown={onFocus}
          onClick={onFocus}
        >
          <div className="dba-chat-ds-report-page-right-container dba-chat-smart-table-container">
            {/* 顶部操作工具栏 - 单行布局 */}
            <div
              className={classNames('dba-chat-smart-table-toolbar', {
                'dba-chat-smart-table-toolbar--draggable': !!onHeaderMouseDown,
              })}
              onMouseDown={(e) => {
                onFocus?.();
                // 如果点击的是右侧操作按钮区域，不触发拖拽
                const isRightArea = (e.target as HTMLElement).closest('.dba-chat-smart-table-toolbar__right');
                if (!isRightArea) {
                  onHeaderMouseDown?.(e);
                }
              }}
            >
              {/* 左侧: 文件选择器 */}
              <div className="dba-chat-smart-table-toolbar__left">
                <FileSelector
                  fileList={fileList}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onChange={handleFileChange}
                  currentFile={currentFile}
                />
              </div>

              {/* 右侧: 操作按钮组 + 分隔线 + 关闭按钮 */}
              <div className="dba-chat-smart-table-toolbar__right">
                <TableActionsBar
                  ref={toolbarRef}
                  gridRef={gridRef}
                  sheetDataMap={sheetDataMap}
                  sheets={sheets}
                  sheetName={activeSheetName}
                  disabled={isLoading}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClose={handleClose}
                  onReset={handleReset}
                  currentFileName={currentFileName}
                  currentFileCosUrl={currentFileCosUrl}
                  readonly={readonly}
                  onSaveSuccess={handleSaveSuccess}
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  aiPolishVisible={aiPolishVisible}
                  onAIPolishClick={handleAIPolishClick}
                  onAIPolishClose={handleAIPolishClose}
                  onAIPolish={handleAIPolish}
                  tableName={tableName}
                />
              </div>
            </div>

            {/* 工作表标签页 */}
            <ExcelSheetTabs
              sheets={sheets}
              activeSheetId={activeSheetId}
              onChange={setActiveSheetId}
              onSheetsChange={handleSheetsChange}
              readonly={readonly}
            />
          </div>

          {/* 表格内容区域 */}
          <div className="dba-chat-smart-table__content-wrapper">
            {/* Loading 遮罩层 */}
            {isLoading && (
              <div className="dba-chat-smart-table__loading-overlay">
                <LoadingTip loadingText={formatMessage({ id: 'aigc_common_loading' })} />
              </div>
            )}

            {/* Handsontable 表格 - 撤销/重做状态已下沉到组件内部 */}
            <SmartTableGrid
              ref={gridRef}
              initialData={tableData}
              initialColumns={columns}
              toolbarRef={toolbarRef}
              tableName={tableName}
              sheetName={activeSheetName}
              onDataChange={handleDataChange}
              onAIModifyColumnCondition={handleAIModifyColumnCondition}
              onAIAdjustDataFormat={handleAIAdjustDataFormat}
              onSmartFillRow={handleSmartFillRow}
              onSmartFillColumn={handleSmartFillColumn}
              readonly={readonly}
            />


          </div>
        </div>
      </>
    );
  },
  areSmartTablePropsEqual,
);

export default ChatSmartTable;
